
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export const TestAnalysisButton = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);

  const handleTestAnalysis = async () => {
    setIsLoading(true);
    setLastResult(null);
    try {
      console.log('=== STARTING TEST ANALYSIS ===');
      console.log('Calling test-search-analysis function...');
      
      const response = await supabase.functions.invoke('test-search-analysis', {
        body: { test: true, timestamp: new Date().toISOString() }
      });

      console.log('=== RESPONSE RECEIVED ===');
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Response error:', response.error);

      setLastResult(response);

      if (response.error) {
        console.error('=== SUPABASE FUNCTION ERROR ===');
        console.error('Error details:', response.error);
        toast.error(`Functie fout: ${response.error.message || 'Onbekende fout'}`);
        return;
      }

      if (response.data?.success) {
        console.log('=== SUCCESS ===');
        toast.success("✅ Test analyse succesvol! Check je e-mail inbox (en spam folder).");
        
        // Check for email ID in nested structure
        const emailId = response.data.testEmailId || 
                       response.data.analysisResult?.testEmailId ||
                       response.data.data?.id ||
                       'Geen ID ontvangen';
        
        if (emailId && emailId !== 'Geen ID ontvangen') {
          toast.success(`📧 E-mail verzonden met ID: ${emailId}`);
        }
      } else {
        console.error('=== ANALYSIS FAILED ===');
        console.error('Failure reason:', response.data);
        toast.error(`❌ Test mislukt: ${response.data?.error || 'Onbekende fout'}`);
      }
      
    } catch (error: any) {
      console.error('=== CATCH ERROR ===');
      console.error('Catch error:', error);
      setLastResult({ error: error.message });
      toast.error(`🚨 Fout: ${error.message || 'Onbekende fout'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
      <div className="flex items-center gap-3 mb-4">
        <Mail className="h-6 w-6 text-blue-600" />
        <h3 className="font-semibold text-blue-800 text-lg">Test E-mail Systeem</h3>
      </div>
      
      <p className="text-blue-700 text-sm mb-4">
        Test het volledige e-mail systeem en ontvang een bevestigingsmail op melvin@y-catcher.nl
      </p>
      
      <Button 
        onClick={handleTestAnalysis}
        disabled={isLoading}
        className="bg-blue-600 hover:bg-blue-700 mb-4"
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Test uitvoeren...
          </>
        ) : (
          <>
            <Mail className="h-5 w-5 mr-2" />
            🧪 Test E-mail Verzenden
          </>
        )}
      </Button>

      {lastResult && (
        <div className="mt-4 p-4 bg-white rounded-lg border">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            {lastResult.data?.success ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            Laatste Test Resultaat
          </h4>
          
          <div className="text-sm space-y-2">
            <div>
              <strong>Status:</strong> 
              <span className={lastResult.data?.success ? "text-green-600 ml-2" : "text-red-600 ml-2"}>
                {lastResult.data?.success ? "✅ Succesvol" : "❌ Mislukt"}
              </span>
            </div>
            
            {/* Check multiple possible locations for email ID */}
            {(() => {
              const emailId = lastResult.data?.testEmailId || 
                             lastResult.data?.analysisResult?.testEmailId ||
                             lastResult.data?.data?.id;
              return emailId ? (
                <div>
                  <strong>E-mail ID:</strong> 
                  <code className="bg-gray-100 px-2 py-1 rounded ml-2 text-xs">
                    {emailId}
                  </code>
                </div>
              ) : null;
            })()}
            
            {/* Show search data count if available */}
            {(() => {
              const searchCount = lastResult.data?.searchDataCount || 
                                 lastResult.data?.analysisResult?.searchDataCount;
              return searchCount !== undefined ? (
                <div>
                  <strong>Zoekdata records:</strong> 
                  <span className="ml-2">{searchCount}</span>
                </div>
              ) : null;
            })()}
            
            {/* Show analysis statistics if available */}
            {lastResult.data?.analysisResult?.statistics && (
              <div>
                <strong>Statistieken:</strong>
                <div className="ml-4 mt-1 text-xs space-y-1">
                  <div>• Totaal queries: {lastResult.data.analysisResult.statistics.totalQueries}</div>
                  <div>• Totaal zoekacties: {lastResult.data.analysisResult.statistics.totalSearches}</div>
                  <div>• Gemiddelde CTR: {lastResult.data.analysisResult.statistics.averageCTR}%</div>
                </div>
              </div>
            )}
            
            {/* Show analysis results if available */}
            {lastResult.data?.analysisResult?.analysis && (
              <div>
                <strong>Analyse resultaat:</strong>
                <div className="ml-4 mt-1 text-xs">
                  <div>• {lastResult.data.analysisResult.analysis.summary}</div>
                  {lastResult.data.analysisResult.lowPerformingCount > 0 && (
                    <div>• Slecht presterende queries: {lastResult.data.analysisResult.lowPerformingCount}</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Show errors */}
            {(lastResult.error || lastResult.data?.error) && (
              <div>
                <strong>Fout:</strong> 
                <span className="text-red-600 ml-2">
                  {lastResult.error?.message || lastResult.data?.error}
                </span>
              </div>
            )}
            
            {/* Show timestamp */}
            {(() => {
              const timestamp = lastResult.data?.timestamp || 
                               lastResult.data?.analysisResult?.timestamp ||
                               lastResult.data?.debug?.timestamp;
              return timestamp ? (
                <div>
                  <strong>Tijdstip:</strong> 
                  <span className="ml-2 text-xs text-gray-600">
                    {new Date(timestamp).toLocaleString('nl-NL')}
                  </span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Raw data for debugging */}
          <details className="mt-3">
            <summary className="text-xs text-gray-500 cursor-pointer">Debug informatie (klik om uit te klappen)</summary>
            <pre className="text-xs bg-gray-50 p-2 rounded mt-2 overflow-auto max-h-40">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <div className="mt-4 text-xs text-blue-600 bg-blue-100 p-3 rounded">
        <strong>💡 Tip:</strong> Als je geen e-mail ontvangt, controleer je spam/ongewenst folder. 
        De e-mail komt van "onboarding@resend.dev".
      </div>
    </div>
  );
};
